import { ThemeProvider } from "@emotion/react";
import { Grid, Typography, FormControl, TextField, Paper, Button, TablePagination, createTheme, FormControlLabel, InputLabel, MenuItem, Select, Switch, Skeleton, Tooltip, Theme, useMediaQuery } from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { PageHeader } from "../../components/PageHeader";
import { useStyles } from "../../common/appStyles";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { PageEnum, RoleEnum, Predicates } from "../../types/enums";
import { useLocation, useNavigate } from "react-router-dom";
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { IRegionDetail } from "../../types/regions";
import { GetCaseDetailData, GetRegionList } from "../../services/user-service";
import { showSnackbar } from "../../services/snackbar.service";
import { SubmissionDetail } from "../Youth/SubmissionDetail";
import { IBasicUserDashboardInfo, ISubmissionState, IUserCompetencySubmissionInformation } from "../../types/youths";
import { ICaseDetail, ICaseDetailInformation } from "../../types/judge";
import { ICaseDetailExpressionRequest, sortingRequest } from "../../types/expression";
import React from "react";
import { RedirectToDash } from "../RedirectToDash";
import { Protoexpression } from "../../utilities/ProtoExpression";
import { updateSubmissionDialog } from "../../store/submissionDialogState";
import { BannerAd } from "../../components/main-layout/BannerAd";

/**
 * @remarks where High level users of the system will view any cases that need to be judged, assigned,
 * have been approved, or have been denied.
 * @returns - html page with a table containing all cases sorted into particular categories
 */
export const CaseDetail = () => {
    const casesInfo = useLocation().state;
    const classes = useStyles();
    const navigate = useNavigate();
    const userRole = useSelector((state: RootState) => state.roles);
    const currentPage = userRole.userPageAccess[PageEnum.CaseDetail];
    const user = useSelector((state: RootState) => state.userDetail);
    const [noResult, setNoResult] = useState(false);
    const [regions, setRegions] = useState<IRegionDetail[]>([]);
    const [selectedRegions, setSelectedRegions] = useState<string[]>(casesInfo?.region ? casesInfo?.region : []);
    const [searchQuery, setSearchQuery] = useState(casesInfo?.searchQuery || "")
    const [selectedCompetency, setSelectedCompetency] = useState<number[]>(casesInfo?.competency);
    const [selectedCaseType, setSelectedCaseType] = useState<string[]>(casesInfo?.caseType);
    const [showSubmissionDetail, setShowSubmissionDetail] = useState(false);
    const [titleText, setTitleText] = useState("");
    const [checked, setChecked] = useState(casesInfo?.assigned);
    const [selectedUser, setSelectedUser] = useState<IBasicUserDashboardInfo>();
    const [selectedSubmission, setSelectedSubmission] = useState<IUserCompetencySubmissionInformation>();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [rows, setRows] = useState<ICaseDetailInformation[]>([]);
    const [count, setCount] = useState<number>();
    const [isLoading, setIsLoading] = useState(false);
    const [sortingBy, setSortingBy] = useState<sortingRequest>();
    const [currentSortModel, setCurrentSortModel] = useState([]);
    const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
    const dispatch = useDispatch();
    const submissionisOpenState = useSelector((state: RootState) => state.submissionDialog);
    const theme = createTheme({
        palette: {
            primary: {
                main: '#484A57'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            body1: {
                color: 'black'
            }
        },
    });

    const formatDate = (input) => {
        const date = input instanceof Date ? input : new Date(input);

        if (isNaN(date.getTime())) {
            console.error('Invalid input for formatDate:', input);
            return new Date(NaN); // Return an invalid Date object
        }

        const formattedDate = new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        }).format(date);
        return formattedDate;
    }

    const columns: GridColDef[] = [
        {
            field: 'firstName',
            headerName: 'Youth',
            headerClassName: classes.DataHead,
            width: 120,
            filterable: false,
            hideable: false,
            renderCell: (params) => (
                <Tooltip title="View User" arrow disableInteractive
                    slotProps={{
                        popper: {
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, -8],
                                    },
                                },
                            ],
                        },
                    }}>
                    <a onClick={(event) => { event.preventDefault(); handleNav(params.row.userId) }} href="#" target="_blank" rel="noopener noreferrer">
                        {params.row.lastName + " " + params.row.firstName}
                    </a>
                </Tooltip>
            ),
        },
        {
            field: 'email',
            headerName: 'Email',
            headerClassName: classes.DataHead,
            width: 200, filterable: false,
            hideable: false,
        },
        {
            field: 'school',
            headerName: 'School',
            headerClassName: classes.DataHead,
            width: 130,
            filterable: false,
            hideable: false,
        },
        {
            field: 'region',
            headerName: 'Region',
            headerClassName: classes.DataHead,
            width: 110,
            filterable: false,
            hideable: false,
        },
        {
            field: 'submissionSubmitDateTime',
            headerName: 'Submission Date',
            headerClassName: classes.DataHead,
            width: 150,
            filterable: false,
            hideable: false,
            renderCell: (params) => (
                <div>
                    {formatDate(params.value)}
                </div>
            ),
        },
        {
            field: 'caseType',
            headerName: 'Case Type',
            headerClassName: classes.DataHead,
            width: 140, filterable: false,
            hideable: false,
        },
        {
            field: 'competency',
            headerName: 'Competency',
            headerClassName: classes.DataHead,
            width: 140, filterable: false,
            hideable: false,
        },
        {
            field: 'title',
            headerName: 'Submission',
            headerClassName: classes.DataHead,
            width: 230,
            filterable: false,
            hideable: false,
            sortable: false
        },
        {
            field: 'actions',
            headerName: 'Actions',
            headerClassName: classes.DataHead,
            width: 100, filterable: false,
            align: 'center',
            headerAlign: 'center',
            hideable: false,
            sortable: false,
            renderCell: (params) => (
                <strong>
                    <Tooltip title="view submission" arrow disableInteractive
                        slotProps={{
                            popper: {
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, -8],
                                        },
                                    },
                                ],
                            },
                        }}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={(event) => {
                                handleView(params.row);
                            }}
                        >
                            View
                        </Button>
                    </Tooltip>
                </strong>
            ),
        },
    ];

    const handleClose = () => {
        const current: ISubmissionState = { isOpen: true };
        dispatch(updateSubmissionDialog(current));
        refreshCaseDetail();
        setShowSubmissionDetail(false);
    }

    const handleView = (row) => {
        setSelectedUser(
            {
                userId: row.userId,
                firstName: row.firstName,
                lastName: row.lastName,
                schoolId: row.schoolId,
                school: row.school,
                regionId: row.regionId,
                region: row.region
            }
        )
        setSelectedSubmission(
            {
                competencyId: row.competencyId,
                competency: row.competency,
                title: row.title,
                submissionCreateDateTime: row.submissionCreateDateTime,
                submissionSubmitDateTime: row.submissionSubmitDateTime,
                isAwarded: row.isAwarded,
                submissionStage: row.submissionStage,
                submissionDecisionDateTime: row.submissionDecisionDateTime,
                submissionId: row.submissionId,
                judgeUserId: row.judgeUserId,
                judgeFeedback: row.judgeFeedback,
                judge2UserId: row.judge2UserId,
                judge2Feedback: row.judge2Feedback
            }
        )
        setTitleText("Judge a Submission");
        setShowSubmissionDetail(true);
    }

    const handleNav = (id: number) => {
        navigate(`/youthdashboard`, { state: { id: id } });
    }
    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
        handleSearch(buildExpression(rowsPerPage, newPage));
    }

    const handleChangeRowsPerPage = (value) => {
        setRowsPerPage(value);
        handleSearch(buildExpression(value));
        setPage(0);
    }

    const handleSearch = async (expression: ICaseDetailExpressionRequest) => {
        setIsLoading(true);
        try {
            const response: ICaseDetail = await GetCaseDetailData(expression);
            setRows(response.caseDetailInformation);
            setCount(response.count);
            setNoResult(response.count === 0);
            if (response.count < page * rowsPerPage) {
                setPage(0)
            }
        } catch (error) {
            showSnackbar("error fetching results", "error", 5000)
        }
        finally {
            setIsLoading(false);
        }
    }

    const buildExpression = (perPage?: number, p?: number, sortingModel?: sortingRequest) => {
        let expression: ICaseDetailExpressionRequest = setDefaultExpression(perPage, p, sortingModel);
        if (Array.isArray(selectedRegions) && selectedRegions.length > 0) {
            expression.region.protoexpressions.pop();
            if (typeof selectedRegions[0] === "string") {
                selectedRegions.forEach(reg => {
                    const id = JSON.parse(reg).id;
                    expression.region.protoexpressions.push(
                        Protoexpression("RegionId", id, Predicates.Equals)
                    )
                });
            }
            else {
                selectedRegions.forEach(reg => {
                    const id = reg;
                    expression.region.protoexpressions.push(
                        Protoexpression("RegionId", id, Predicates.Equals)
                    )
                });
            }
        }
        if (Array.isArray(selectedCompetency) && selectedCompetency.length > 0) {
            expression.competency.protoexpressions.pop();

            selectedCompetency.forEach(compId => {
                expression.competency.protoexpressions.push(
                    Protoexpression("CompetencyId", compId, Predicates.Equals)
                )
            });
        }
        if (Array.isArray(selectedCaseType) && selectedCaseType.length > 0) {
            expression.caseType.protoexpressions.pop();

            selectedCaseType.forEach(type => {
                expression.caseType.protoexpressions.push(
                    Protoexpression("CaseType", type, Predicates.Equals)
                )
            });
        }
        return expression;
    }

    const setDefaultExpression = (perPage?: number, p?: number, sortingModel?: sortingRequest) => {
        const expression: ICaseDetailExpressionRequest =
        {
            searchRequest: {
                expressionRequest: {
                    protoexpressions: [
                        Protoexpression("Email", searchQuery || "", Predicates.StartsWith)
                    ]
                },
                paginationRequest: {
                    entriesPerPage: perPage || rowsPerPage,
                    pageNumber: p === undefined ? page + 1 : p + 1
                }
            },
            region: {
                protoexpressions: [
                    Protoexpression("Region", "", Predicates.StartsWith)
                ]
            },
            caseType: {
                protoexpressions: [
                    Protoexpression("CaseType", "", Predicates.StartsWith)
                ]
            },
            competency: {
                protoexpressions: [
                    Protoexpression("Competency", "", Predicates.StartsWith)
                ]
            },
            sorting: sortingModel ? sortingModel : sortingBy ? sortingBy : {
                fieldName: "SubmissionSubmitDateTime",
                sort: "asc"
            },
            assignedOnly: checked
        }
        return expression;
    }

    const capitalizeFirst = (str) => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const createSortModel = (sortInfo?: sortingRequest) => {
        if (sortInfo) {
            if (sortInfo.fieldName === "firstName") {
                sortInfo.fieldName = "LastName";
            }
            else {
                sortInfo.fieldName = capitalizeFirst(sortInfo.fieldName);
            }
            setSortingBy(
                {
                    fieldName: sortInfo.fieldName,
                    sort: sortInfo.sort
                }
            );
            return sortInfo;
        }
        else {
            setSortingBy(
                {
                    fieldName: "SubmissionSubmitDateTime",
                    sort: "asc"
                }
            );
            return {
                fieldName: "SubmissionSubmitDateTime",
                sort: "asc"
            }
        }
    }

    const getAllRegionsList = () => {
        GetRegionList()
            .then(response => {
                setRegions(response);
            })
            .catch((error) => {
                showSnackbar('Error Retrieving data', 'error', 5000);
            });
    }

    const handleRegion = (value) => {
        setSelectedRegions(value);
    }

    const handleCaseType = (value) => {
        setSelectedCaseType(value);
    }

    const handleCompetency = (value) => {
        setSelectedCompetency(value);
    }

    const handleChecked = (value) => {
        setChecked(value);
    }

    const handleBack = () => {
        navigate(-1);
    }

    const refreshCaseDetail = () => {
        handleSearch(buildExpression());
    }

    const handleSortModelChange = React.useCallback((sortModel) => {
        setCurrentSortModel(sortModel);
        if (Array.isArray(sortModel) && sortModel.length !== 0) {
            const model: sortingRequest = {
                fieldName: sortModel[0].field,
                sort: sortModel[0].sort
            };
            handleSearch(buildExpression(rowsPerPage, page, createSortModel(model)))
        }
    }, []);

    useEffect(() => {
        if (currentPage?.read) {
            setSelectedRegions(casesInfo?.region)
            getAllRegionsList();
            setSearchQuery(casesInfo?.searchQuery);
            setSelectedCaseType(casesInfo?.caseType);
            setSelectedCompetency(casesInfo?.competency);
            setChecked(casesInfo?.assigned);
            if (user.roleId === RoleEnum.Judge) {
                setChecked(true);
            }
            const expression: ICaseDetailExpressionRequest = buildExpression();
            handleSearch(expression)
        }
    }, [currentPage, user, casesInfo]);

    useEffect(() => {
        if (Array.isArray(selectedRegions) && regions.length !== 0) {
            const filteredRegions: string[] = [];
            selectedRegions.forEach(reg => {
                if (typeof reg === "number") {
                    const matchingRegion = regions.find(reggie => reggie.id === reg);
                    if (matchingRegion) {
                        filteredRegions.push(JSON.stringify(matchingRegion));
                    }
                }
            });
            setSelectedRegions(filteredRegions);
        }
    }, [regions]);

    return (
        <Fragment>
            <ThemeProvider theme={theme}>
                <Grid
                    container
                    className={classes.formContainer}
                    spacing={1}
                    justifyContent="center"
                    alignItems={'center'}
                >
                    {user.roleId >= RoleEnum.CredentialingManager && !lgUp && <Grid className={classes.bannerTopPadding}>
                        <BannerAd />
                    </Grid>}
                    <Grid item sm={12} className='justify-center' padding="5px">
                        {currentPage?.read &&
                            <Grid
                                container
                                spacing={1}
                                justifyContent="center"
                                alignItems="center"
                                className={classes.fixGridMargin}
                            >
                                <Grid item xs={12} sx={{ minWidth: "100%" }}>
                                    <Grid container alignItems={"center"} justifyContent={"flex-start"} className={classes.buttonPadding}>
                                        <Grid item xs={2}>
                                            <Button variant={"contained"} onClick={handleBack} color="secondary">
                                                Back
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={3}><PageHeader title={"Case Detail"} onBackClick={handleBack} /></Grid>
                                <><Grid item xs={12}>
                                </Grid><Grid
                                    container
                                    justifyContent={'center'}
                                    alignItems={"center"}
                                    spacing={1}
                                    className={classes.gridPaddingTop}
                                >
                                        <Grid item xs={12}>
                                            {(casesInfo && regions) ? <Grid container alignItems={"center"} justifyContent={"center"} columnSpacing={1} direction={{ xs: 'column', md: 'row' }} rowSpacing={2}>
                                                <Grid item xs={3}>
                                                    <FormControl variant='outlined' fullWidth>
                                                        <TextField
                                                            type="text"
                                                            label="Email"
                                                            aria-labelledby="Search Email"
                                                            value={searchQuery}
                                                            onChange={(event) => setSearchQuery(event.target.value)}
                                                        >
                                                        </TextField>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item md={2} xs={12} minWidth={{ xs: '20vh', md: '5vh' }}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Region</InputLabel>
                                                        <Select
                                                            MenuProps={{
                                                                disableScrollLock: true,
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: "15vh",
                                                                    },
                                                                }
                                                            }}
                                                            multiple
                                                            required
                                                            labelId="Region-label"
                                                            label="Region"
                                                            size='medium'
                                                            value={selectedRegions}
                                                            onChange={(event) => {
                                                                handleRegion(event.target.value);
                                                            }}
                                                        >
                                                            {regions.map((reg) => (
                                                                <MenuItem key={reg.id} value={JSON.stringify(reg)} aria-labelledby={reg.name}>{reg.name}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={1.7} minWidth={{ xs: '20vh', md: '5vh' }}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Case Type</InputLabel>
                                                        <Select
                                                            MenuProps={{
                                                                disableScrollLock: true,
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: "20vh",
                                                                    },
                                                                }
                                                            }}
                                                            required
                                                            multiple
                                                            labelId="CaseType-label"
                                                            label="Case Type"
                                                            size='medium'
                                                            value={selectedCaseType}
                                                            onChange={(event) => {
                                                                handleCaseType(event.target.value)
                                                            }}
                                                        >
                                                            <MenuItem value={"Cases For Action"}>Cases For Action</MenuItem>
                                                            <MenuItem value={"Cases In Progress"}>Cases In Progress</MenuItem>
                                                            <MenuItem value={"Cases Completed"}>Cases Completed</MenuItem>
                                                            <MenuItem value={"Cases Denied"}>Cases Denied</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={2} minWidth={{ xs: '20vh', md: '5vh' }}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>Competency</InputLabel>
                                                        <Select
                                                            MenuProps={{
                                                                disableScrollLock: true,
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: "20vh",
                                                                    },
                                                                }
                                                            }}
                                                            required
                                                            labelId="Competency-label"
                                                            label="Competency"
                                                            multiple
                                                            size='medium'
                                                            value={selectedCompetency}
                                                            onChange={(event) => {
                                                                handleCompetency(event.target.value);
                                                            }}
                                                        >
                                                            <MenuItem value={1}>Growth Mindset</MenuItem>
                                                            <MenuItem value={2}>Creative Problem Solving</MenuItem>
                                                            <MenuItem value={3}>Emotional Intelligence</MenuItem>
                                                            <MenuItem value={4}>Coaching</MenuItem>
                                                            <MenuItem value={5}>Social Influence</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                {user.roleId <= RoleEnum.CredentialingManager && <Grid item xs={2}>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={checked}
                                                                onChange={(event) => handleChecked(event.target.checked)}
                                                                name="showAssigned"
                                                                color="secondary"
                                                            />
                                                        }
                                                        label="Assigned To Me"
                                                    />
                                                </Grid>}
                                                <Grid item xs={1}>
                                                    <Button
                                                        variant="contained"
                                                        size="large"
                                                        aria-labelledby="search"
                                                        style={{ height: 55 }}
                                                        color="secondary"
                                                        fullWidth
                                                        onClick={(event) => handleSearch(buildExpression())}
                                                    >
                                                        Search
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                                :
                                                <Grid>
                                                    <Skeleton variant="rectangular" width={'100%'} height={'10vh'} />
                                                </Grid>
                                            }
                                        </Grid>
                                        <Grid item md={11.5} className={classes.fixGridMargin} xs={12} alignSelf={'center'} maxWidth={{ xs: '45vh', sm: '100%', md: '100%' }}>
                                            {(rows !== undefined && rows !== null && !noResult) && <Paper elevation={3}>
                                                {isLoading ?
                                                    <Grid container direction={"column"} rowSpacing={.5}>
                                                        <Grid item xs={12}>
                                                            <Skeleton variant='rectangular' width={"100%"} height={"7vh"} />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Skeleton variant='rounded' width={'100%'} height={'59vh'} />
                                                        </Grid>
                                                    </Grid>
                                                    :
                                                    <Grid>
                                                        <div>
                                                            <DataGrid
                                                                rows={rows}
                                                                columns={columns}
                                                                getRowId={(row) => row.submissionId}
                                                                disableRowSelectionOnClick={true}
                                                                disableMultipleRowSelection
                                                                sortingMode="server"
                                                                sortModel={currentSortModel}
                                                                onSortModelChange={handleSortModelChange}
                                                                hideFooter={true}
                                                                style={{ height: "61vh" }}
                                                            />
                                                        </div>
                                                        <TablePagination
                                                            component="div"
                                                            count={count || 0}
                                                            rowsPerPageOptions={[10, 25, 50]}
                                                            page={page}
                                                            onPageChange={handleChangePage}
                                                            rowsPerPage={rowsPerPage}
                                                            onRowsPerPageChange={(event) => handleChangeRowsPerPage(event.target.value)}
                                                        />
                                                    </Grid>}
                                            </Paper>}
                                        </Grid>
                                        <Grid alignSelf={"center"} className={classes.tableNoResult}>
                                            {noResult && <FormControl fullWidth><Typography variant="h5">No Results Found</Typography></FormControl>}
                                        </Grid>
                                    </Grid></>
                            </Grid>
                        }
                        {!currentPage?.read && currentPage?.read !== undefined &&
                            <RedirectToDash />
                        }
                        {currentPage?.read && <Grid item sm={12}>
                            {showSubmissionDetail && <SubmissionDetail
                                showDialog={showSubmissionDetail}
                                selectedSubmission={selectedSubmission}
                                selectedUser={selectedUser}
                                titleText={titleText}
                                handleClose={handleClose}
                            />}
                        </Grid>}
                    </Grid>
                </Grid>
            </ThemeProvider>
        </Fragment>);
}